// Home — JSON-manifest-driven layout with end-user drag/reorder + hide.
// Layout fetched from `GET /api/home/layout` (resolved per role + per user
// override). Customisation persisted via `PUT /api/home/layout`.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Settings2, RotateCcw, Eye, EyeOff, Check, X } from "lucide-react";
import { toast } from "sonner";
import RGL, { WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { HomeWidget, HOME_WIDGET_REGISTRY } from "@/components/home/HomeWidget";
// iter-121.116 — SA Platform Cockpit scope bar + context provider
import { CockpitScopeProvider } from "@/lib/cockpit-scope";
import CockpitScopeBar from "@/components/home/CockpitScopeBar";
// iter-121.117 — OA Cockpit scope bar + context provider
import { OaScopeProvider } from "@/lib/oa-scope";
import OaScopeBar, { useOaHeader } from "@/components/home/OaScopeBar";
// iter-121.118 — EA Cockpit scope bar + context provider
import { EaScopeProvider } from "@/lib/ea-scope";
import EaScopeBar, { useEaHeader } from "@/components/home/EaScopeBar";

const Grid = WidthProvider(RGL);

// Convert manifest widgets → react-grid-layout `layouts`. The manifest's
// 12-col {row, col, span} maps directly onto RGL's {x, y, w, h}; height
// comes from the registry's per-widget default unless the user resized.
// We let RGL's vertical compaction pack rows tightly — `y` only encodes
// row order within the manifest, not absolute pixel offset.
function manifestToLayout(widgets) {
    const visible = (widgets || []).filter((w) => !w.hidden);
    // Group manifest entries by `row` (1-based) so within-row order is preserved.
    const sortedByRow = [...visible].sort((a, b) => (a.row || 1) - (b.row || 1) || (a.col || 1) - (b.col || 1));
    let cumulativeY = 0;
    let lastRow = null;
    let rowMaxH = 0;
    return sortedByRow.map((w) => {
        const reg = HOME_WIDGET_REGISTRY[w.key];
        const h = w.h || (reg?.defaultH ?? 6);
        const r = w.row || 1;
        if (lastRow !== null && r !== lastRow) {
            cumulativeY += rowMaxH;
            rowMaxH = 0;
        }
        rowMaxH = Math.max(rowMaxH, h);
        lastRow = r;
        return {
            i: w.key,
            x: Math.max(0, (w.col || 1) - 1),
            y: cumulativeY,
            w: Math.max(1, Math.min(12, w.span || 12)),
            h,
            minW: 3,
            minH: 3,
        };
    });
}

// After a user drag/resize, fold the new layout back into manifest shape.
function layoutToManifest(layout, prevWidgets) {
    // Re-derive `row` from the grid-y of each item, sorted ascending.
    const sorted = [...layout].sort((a, b) => (a.y - b.y) || (a.x - b.x));
    const rowOf = new Map();
    let lastY = -1, currentRow = 0;
    sorted.forEach((l) => {
        if (l.y !== lastY) { currentRow += 1; lastY = l.y; }
        rowOf.set(l.i, currentRow);
    });
    // Hidden ones don't appear in `layout` — preserve them.
    const visible = layout.map((l) => ({
        key: l.i,
        row: rowOf.get(l.i) || 1,
        col: l.x + 1,
        span: l.w,
        h: l.h,
        hidden: false,
    }));
    const hidden = (prevWidgets || [])
        .filter((w) => w.hidden)
        .map((w) => ({ ...w, hidden: true }));
    return [...visible, ...hidden];
}

function Toolbar({ editing, onToggle, onReset, onSave, dirty, onDiscard }) {
    return (
        <div className="flex items-center gap-2">
            {editing ? (
                <>
                    <Button size="sm" variant="outline" onClick={onDiscard} data-testid="home-edit-cancel">
                        <X className="h-3.5 w-3.5 mr-1.5" /> Cancel
                    </Button>
                    <Button size="sm" variant="outline" onClick={onReset} data-testid="home-edit-reset">
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset to default
                    </Button>
                    <Button size="sm" onClick={onSave} disabled={!dirty} data-testid="home-edit-save">
                        <Check className="h-3.5 w-3.5 mr-1.5" /> Save layout
                    </Button>
                </>
            ) : (
                <Button size="sm" variant="outline" onClick={onToggle} data-testid="home-edit-toggle">
                    <Settings2 className="h-3.5 w-3.5 mr-1.5" /> Customise
                </Button>
            )}
        </div>
    );
}

export default function Home() {
    const { user } = useAuth();
    const [widgets, setWidgets] = useState(null); // manifest array
    const [original, setOriginal] = useState(null); // for cancel
    const [editing, setEditing] = useState(false);
    const [dirty, setDirty] = useState(false);
    // Widgets that have self-reported "I have nothing to show" — excluded
    // from layout in non-editing mode so they don't leave gaps.
    const [emptyKeys, setEmptyKeys] = useState(() => new Set());

    const fetchLayout = useCallback(async () => {
        try {
            const r = await api.get("/home/layout");
            const arr = (r.data?.layout?.widgets) || [];
            // Normalise: ensure each entry has a `hidden` boolean.
            const norm = arr.map((w) => ({ ...w, hidden: !!w.hidden }));
            setWidgets(norm);
            setOriginal(norm);
            setDirty(false);
        } catch {
            setWidgets([]);
        }
    }, []);

    useEffect(() => { fetchLayout(); }, [fetchLayout]);

    const layout = useMemo(() => manifestToLayout(widgets || []), [widgets]);
    // In VIEW mode, hide widgets that reported empty AND re-compact y-coords
    // so remaining widgets rise to fill the gap. In EDIT mode we keep them
    // visible so the user can manually rearrange/hide them.
    const visibleLayout = useMemo(() => {
        if (editing) return layout;
        const filtered = layout.filter((l) => !emptyKeys.has(l.i));
        if (filtered.length === layout.length) return filtered;
        // Re-compact y-positions: walk in original y order, rebuild row-by-row
        // with zero-based, gap-free y coordinates.
        const sorted = [...filtered].sort((a, b) => (a.y - b.y) || (a.x - b.x));
        const compacted = [];
        let yCursor = 0;
        let prevY = null;
        let rowMaxH = 0;
        for (const l of sorted) {
            if (prevY !== null && l.y !== prevY) {
                yCursor += rowMaxH;
                rowMaxH = 0;
            }
            rowMaxH = Math.max(rowMaxH, l.h);
            compacted.push({ ...l, y: yCursor });
            prevY = l.y;
        }
        return compacted;
    }, [layout, emptyKeys, editing]);

    // Memoised per-key onEmpty callbacks — avoids infinite render loops when
    // a child widget's useEffect depends on this prop.
    const emptyCallbacksRef = React.useRef({});
    const getEmptyCallback = useCallback((key) => {
        if (!emptyCallbacksRef.current[key]) {
            emptyCallbacksRef.current[key] = (isEmpty) => {
                setEmptyKeys((prev) => {
                    const has = prev.has(key);
                    if (isEmpty && !has) {
                        const next = new Set(prev); next.add(key); return next;
                    }
                    if (!isEmpty && has) {
                        const next = new Set(prev); next.delete(key); return next;
                    }
                    return prev;
                });
            };
        }
        return emptyCallbacksRef.current[key];
    }, []);

    const onLayoutChange = (next) => {
        if (!editing || !widgets) return;
        // Only react when geometry actually changed for visible widgets.
        const cur = manifestToLayout(widgets);
        const same = next.length === cur.length && next.every((n) => {
            const m = cur.find((c) => c.i === n.i);
            return m && m.x === n.x && m.y === n.y && m.w === n.w && m.h === n.h;
        });
        if (same) return;
        const merged = layoutToManifest(next, widgets);
        setWidgets(merged);
        setDirty(true);
    };

    const toggleHide = (key) => {
        setWidgets((prev) => prev.map((w) => w.key === key ? { ...w, hidden: !w.hidden } : w));
        setDirty(true);
    };

    const save = async () => {
        try {
            await api.put("/home/layout", { widgets });
            setOriginal(widgets);
            setDirty(false);
            setEditing(false);
            toast.success("Home layout saved");
        } catch (e) {
            toast.error("Failed to save layout");
        }
    };

    const reset = async () => {
        try {
            await api.delete("/home/layout");
            await fetchLayout();
            setEditing(false);
            toast.success("Reset to role default");
        } catch {
            toast.error("Failed to reset");
        }
    };

    const discard = () => {
        setWidgets(original);
        setDirty(false);
        setEditing(false);
    };

    const hiddenWidgets = (widgets || []).filter((w) => w.hidden);
    const firstName = user?.name?.split(" ")[0] || "";
    // iter-121.116 — SA-only Platform Cockpit scope bar above the grid.
    // iter-121.117 — OA Home gets its own scope bar + provider.
    // iter-121.118 — EA Home gets its own scope bar + provider.
    const isSuperAdmin = user?.role === "super_admin";
    const isOrgAdmin = user?.role === "org_admin";
    const isEntityAdmin = user?.role === "entity_admin";
    const greeting = firstName
        ? `Good ${(() => {
              const h = new Date().getHours();
              return h < 12 ? "morning" : h < 18 ? "afternoon" : "evening";
          })()}, ${firstName}`
        : "Welcome back";

    const gridBlock = (
        <div className="fadein">
            <PageHeader
                eyebrow={isSuperAdmin
                    ? "Platform Cockpit"
                    : isOrgAdmin
                        ? "Org Command Center"
                        : isEntityAdmin
                            ? "Entity Command Center"
                            : (firstName ? `Welcome back, ${firstName}` : "Welcome back")}
                title={isSuperAdmin
                    ? "Platform Cockpit"
                    : isOrgAdmin
                        ? "Org Command Center"
                        : isEntityAdmin
                            ? "Entity Command Center"
                            : "Command Center"}
                description={isSuperAdmin
                    ? "Two perspectives — what needs your action and how the platform is performing."
                    : isOrgAdmin
                        ? "Two perspectives — what needs your action and how your organisation is performing."
                        : isEntityAdmin
                            ? "Two perspectives — what needs your action and how your entity is performing."
                            : "Your unified view of data, dashboards, and AI analytics."}
                testId="home-header"
                actions={
                    <Toolbar
                        editing={editing}
                        onToggle={() => setEditing(true)}
                        onReset={reset}
                        onSave={save}
                        onDiscard={discard}
                        dirty={dirty}
                    />
                }
            />
            <PageContent>
                {isSuperAdmin && <CockpitScopeBar greeting={greeting} />}
                {isOrgAdmin && <OaScopeBarBlock userName={firstName} />}
                {isEntityAdmin && <EaScopeBarBlock userName={firstName} />}
                {widgets === null ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="h-24 shimmer rounded-sm" />
                        ))}
                    </div>
                ) : widgets.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-12 text-center" data-testid="home-empty">
                        No widgets configured for your role.
                    </div>
                ) : (
                    <>
                        {editing && (
                            <div className="mb-4 border border-primary/40 bg-primary/5 rounded-sm p-3 flex items-start gap-3"
                                 data-testid="home-edit-banner">
                                <Settings2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                                <div className="flex-1 text-[13px]">
                                    <div className="font-medium">Customise mode</div>
                                    <div className="text-muted-foreground text-xs mt-0.5">
                                        Drag the move handle to reorder · drag the bottom-right corner to resize · use the eye icon to hide widgets · Save when done.
                                    </div>
                                </div>
                            </div>
                        )}

                        <Grid
                            className="layout"
                            layout={visibleLayout}
                            cols={12}
                            rowHeight={28}
                            margin={[10, 10]}
                            isDraggable={editing}
                            isResizable={editing}
                            draggableHandle=".home-widget-drag-handle"
                            onLayoutChange={onLayoutChange}
                            useCSSTransforms={true}
                            compactType="vertical"
                        >
                            {visibleLayout.map((l) => {
                                const w = (widgets || []).find((x) => x.key === l.i);
                                return (
                                    <div key={l.i} className="relative" data-testid={`home-slot-${l.i}`}>
                                        {editing && (
                                            <>
                                                <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between bg-card/95 backdrop-blur border-b border-border pl-2 pr-9 py-1 home-widget-drag-handle cursor-move rounded-t-sm">
                                                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                                                        {HOME_WIDGET_REGISTRY[l.i]?.label || l.i}
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onMouseDown={(e) => { e.stopPropagation(); }}
                                                    onClick={(e) => { e.stopPropagation(); toggleHide(l.i); }}
                                                    className="absolute top-1 right-1 z-30 text-muted-foreground hover:text-primary p-1 bg-card rounded-sm border border-transparent hover:border-border"
                                                    title="Hide widget"
                                                    data-testid={`home-hide-${l.i}`}>
                                                    <EyeOff className="h-3.5 w-3.5" />
                                                </button>
                                            </>
                                        )}
                                        <div className={editing ? "pt-7 h-full" : "h-full"}>
                                            <HomeWidget widgetKey={l.i} onEmpty={getEmptyCallback(l.i)} />
                                        </div>
                                    </div>
                                );
                            })}
                        </Grid>

                        {editing && hiddenWidgets.length > 0 && (
                            <div className="mt-6 border border-border rounded-sm bg-card p-4" data-testid="home-hidden-tray">
                                <div className="eyebrow text-[10px] mb-2">Hidden widgets · click to restore</div>
                                <div className="flex flex-wrap gap-2">
                                    {hiddenWidgets.map((w) => (
                                        <button key={w.key}
                                            onClick={() => toggleHide(w.key)}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-border rounded-sm text-xs hover:border-primary hover:bg-primary/5"
                                            data-testid={`home-restore-${w.key}`}>
                                            <Eye className="h-3.5 w-3.5" />
                                            {HOME_WIDGET_REGISTRY[w.key]?.label || w.key}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </PageContent>
        </div>
    );

    // Wrap in the scope provider ONLY for super_admin / org_admin /
    // entity_admin — other roles never call the cockpit endpoints, so
    // the provider has nothing to do.
    if (isSuperAdmin) {
        return <CockpitScopeProvider>{gridBlock}</CockpitScopeProvider>;
    }
    if (isOrgAdmin) {
        return <OaScopeProvider>{gridBlock}</OaScopeProvider>;
    }
    if (isEntityAdmin) {
        return <EaScopeProvider>{gridBlock}</EaScopeProvider>;
    }
    return gridBlock;
}

// Helper component that fetches the OA header (greeting subtitle + trial
// state) and renders the OaScopeBar. Lives inside the OaScopeProvider so
// it can call `useOaHeader` and share trial state with action lane.
function OaScopeBarBlock({ userName }) {
    const { data } = useOaHeader();
    return (
        <OaScopeBar
            userName={userName}
            orgName={data?.org_name}
            dynamicSubtitle={data?.dynamic_subtitle}
            trialState={data?.trial_state}
        />
    );
}

// iter-121.118 — EA analogue. Lives inside EaScopeProvider so the
// `useEaHeader` hook can fetch the header data once for the scope bar.
function EaScopeBarBlock({ userName }) {
    const { data } = useEaHeader();
    return (
        <EaScopeBar
            userName={userName}
            tenantName={data?.tenant_name}
            dynamicSubtitle={data?.dynamic_subtitle}
        />
    );
}
